import * as sections from '@shopify/theme-sections';
import getProduct from '@/core/helpers/getProduct';
import { moneyWithCurrencyProvided } from '@/filters';

const SECTION_NAME = 'product-pre-mapped-ck';

const ACTIVE_CURRENCY = window?.Shopify?.currency?.active ?? 'USD';

const selectors = {
    mainSliderDesktop: '[data-product-images-slider-desktop]',
    mainSliderMobile: '[data-product-images-slider-mobile]',
    thumbsSlider: '[data-product-thumbnails-carousel]',
    paginationMobile: '[data-product-images-pagination]',
    reviewsSlider: '[data-featured-reviews-slider]',
    reviewsPagination: '[data-featured-reviews-pagination]',
    sliderHtml: '[data-slider-html]',
    optionValue: '[data-product-option-value]',
    saleRetailPrice: '[data-sale-retail-price]',
    saleCompareAtPrice: '[data-sale-retail-compare-at-price]',
    saleTag: '[data-sale-tag]',
    saleDiscountPercent: '[data-sale-discount-percent]',
    buyVariantId: '[data-buy-variant-id]',
    buySubmit: '[data-buy-submit]',
    buySubmitText: '[data-buy-submit-text]',
    buyBlock: '[data-buy-block]',
    tutorialMainBtn: '.product-steps__tutorial-btn',
    tutorialFloatingBtn: '.product-steps__floating-btn',
};

function ProductSection(container) {
    return {
        container,
        sliders: {},
        dependencies: {},
        product: null,
        currentVariant: null,
        selectedOptions: [],
        onOptionChangeHandler: null,
        buyBoxTracking: null,

        async onLoad() {
            this.initBuyBoxTracking();
            await this.initDependencies();
            this.initSlider();
            this.initReviewsSlider();
            await this.initOptions();
        },

        async initDependencies() {
            const [
                { default: Swiper },
                { EffectFade, Navigation, Pagination, Thumbs, Zoom, Keyboard },
            ] = await Promise.all([
                import('swiper'),
                import('swiper/modules'),
            ]);

            this.dependencies = {
                Swiper,
                EffectFade,
                Navigation,
                Pagination,
                Thumbs,
                Zoom,
                Keyboard,
            };
        },

        initSlider() {
            const { container } = this;
            if (!container) {
                return;
            }

            const mainSlider = container.querySelector(selectors.mainSliderDesktop);
            if (!mainSlider) {
                return;
            }

            this.initThumbsSlider();

            const thumbs = this.sliders.thumbsSlider
                ? { swiper: this.sliders.thumbsSlider }
                : null;

            const pagination = document.querySelector(selectors.paginationMobile);

            const swiperOptions = {
                initialSlide: 0,
                slidesPerView: 1,
                spaceBetween: 0,
                draggable: true,
                allowTouchMove: true,
                touchEventsTarget: 'container',
                keyboard: { enabled: true },
            };

            this.sliders.mainSlider = new this.dependencies.Swiper(mainSlider, {
                ...swiperOptions,
                loop: true,
                effect: 'fade',
                fadeEffect: { crossFade: true },
                zoom: {
                    enabled: true,
                    minRatio: 1,
                    maxRatio: 1,
                    toggle: true,
                },
                breakpoints: {
                    1025: {
                        zoom: {
                            enabled: true,
                            minRatio: 1,
                            maxRatio: 1.75,
                            toggle: true,
                        },
                    },
                },
                thumbs,
                modules: [
                    this.dependencies.Zoom,
                    this.dependencies.Thumbs,
                    this.dependencies.EffectFade,
                    this.dependencies.Keyboard,
                    this.dependencies.Pagination,
                ],
                on: {
                    afterInit(swiper) {
                        swiper?.el?.addEventListener('click', () => {
                            swiper?.zoom?.toggle();
                        });
                    },
                    resize(swiper) {
                        try { swiper?.zoom?.out(); } catch {}
                    },
                    sliderMove(swiper) {
                        try { swiper?.zoom?.out(); } catch {}
                    },
                    slideChange(swiper) {
                        const activeSlide = swiper?.slides?.[swiper.activeIndex];
                        const video = activeSlide?.querySelector('video[data-video-autoplay][muted]:not(.initialized)');
                        if (video) {
                            video.play();
                            video.classList.add('initialized');
                        }
                        try { swiper?.zoom?.out(); } catch {}
                    },
                },
            });

            const mobileSlider = document.querySelector(selectors.mainSliderMobile);
            if (!mobileSlider) {
                return;
            }

            this.sliders.mobileSlider = new this.dependencies.Swiper(mobileSlider, {
                ...swiperOptions,
                effect: 'slide',
                slidesPerView: 1,
                spaceBetween: 0,
                ...(pagination
                    ? {
                          pagination: {
                              el: pagination,
                              type: 'custom',
                              clickable: true,
                              renderCustom(swiper, current, total) {
                                  let html = '';
                                  for (let index = 1; index <= total; index += 1) {
                                      const classes = ['swiper-pagination-bullet'];
                                      if (index === current) {
                                          classes.push('swiper-pagination-bullet-active');
                                      }
                                      html += `<span class="${classes.join(' ')}" data-index="${index}"></span>`;
                                  }
                                  return html;
                              },
                          },
                      }
                    : {}),
                modules: [
                    this.dependencies.Pagination,
                    this.dependencies.Keyboard,
                    this.dependencies.Zoom,
                ],
                on: {
                    slideChange(swiper) {
                        const activeSlide = swiper.slides[swiper.activeIndex];
                        const video = activeSlide?.querySelector('video[data-video-autoplay][muted]:not(.initialized)');
                        if (video) {
                            video.play();
                            video.classList.add('initialized');
                        }
                        try { swiper?.zoom?.out(); } catch {}
                    },
                },
            });
        },

        initReviewsSlider() {
            const { container } = this;
            if (!container) {
                return;
            }

            const reviewsSlider = container.querySelector(selectors.reviewsSlider);
            if (!reviewsSlider) {
                return;
            }

            const pagination = container.querySelector(selectors.reviewsPagination);
            const bullets = pagination
                ? Array.from(pagination.querySelectorAll('[data-index]'))
                : [];

            const setActiveBullet = (swiper) => {
                if (!bullets.length) {
                    return;
                }

                const index = swiper.isEnd ? bullets.length - 1 : swiper.activeIndex;

                bullets.forEach((bullet, i) => {
                    bullet.classList.toggle('is-active', i === index);
                });
            };

            this.sliders.reviewsSlider = new this.dependencies.Swiper(reviewsSlider, {
                slidesPerView: 1.2,
                spaceBetween: 8,
                allowTouchMove: true,
                breakpoints: {
                    1025: {
                        slidesPerView: 1.66,
                    },
                },
                on: {
                    init: setActiveBullet,
                    slideChange: setActiveBullet,
                },
            });

            if (pagination && !pagination.dataset.bound) {
                pagination.dataset.bound = 'true';
                pagination.addEventListener('click', (event) => {
                    const bullet = event.target.closest('[data-index]');
                    if (!bullet) {
                        return;
                    }

                    this.sliders.reviewsSlider?.slideTo(Number(bullet.dataset.index) || 0);
                });
            }
        },

        initThumbsSlider() {
            const { container } = this;
            if (!container) {
                return;
            }

            const thumbsSlider = container.querySelector(selectors.thumbsSlider);
            if (!thumbsSlider) {
                return;
            }

            this.sliders.thumbsSlider = new this.dependencies.Swiper(thumbsSlider, {
                initialSlide: 0,
                direction: 'vertical',
                spaceBetween: 10,
                slidesPerView: 4,
                loop: false,
                draggable: true,
                allowTouchMove: true,
                navigation: {
                    nextEl: '[data-product-thumbs-next]',
                    prevEl: '[data-product-thumbs-prev]',
                },
                modules: [this.dependencies.Navigation],
            });
        },

        async initOptions() {
            const { container } = this;
            if (!container) {
                return;
            }

            const optionInputs = container.querySelectorAll(selectors.optionValue);
            if (!optionInputs.length) {
                return;
            }

            const handle = container.dataset.productHandle;
            if (!handle) {
                return;
            }

            const currentVariantId = new URL(window.location.href).searchParams.get('variant');

            try {
                this.product = await getProduct(handle, currentVariantId);
            } catch {
                return;
            }

            if (!this.product?.variants?.length) {
                return;
            }

            this.currentVariant = this.product.selected_or_first_available_variant
                || this.product.first_available_variant;
            this.selectedOptions = [...(this.currentVariant?.options || [])];

            container
                .querySelectorAll(`${selectors.optionValue}:checked`)
                .forEach((input) => {
                    const position = parseInt(input.dataset.optionPosition);
                    if (position) {
                        this.selectedOptions[position - 1] = input.dataset.optionValue;
                    }
                });

            const resolved = this.findVariant(this.selectedOptions);
            if (resolved) {
                this.currentVariant = resolved;
            }

            this.onOptionChangeHandler = (event) => {
                const input = event.target.closest(selectors.optionValue);
                if (input?.checked) {
                    this.onOptionChange(input);
                }
            };

            container.addEventListener('change', this.onOptionChangeHandler);
        },

        async onOptionChange(input) {
            const position = parseInt(input.dataset.optionPosition);
            const value = input.dataset.optionValue;
            if (!position || value == null) {
                return;
            }

            this.selectedOptions[position - 1] = value;

            const variant = this.resolveVariant(position - 1, value);
            if (!variant) {
                return;
            }

            this.currentVariant = variant;
            this.selectedOptions = [...variant.options];

            this.updateURL();
            this.updatePrices();
            this.updateBuyForm();
            await this.updateSlider();
        },

        findVariant(options) {
            return this.product?.variants?.find((variant) =>
                options.every((value, index) => variant.options[index] === value)
            );
        },

        resolveVariant(changedIndex, value) {
            return (
                this.findVariant(this.selectedOptions)
                || this.product?.variants?.find(
                    (variant) => variant.options[changedIndex] === value && variant.available
                )
                || this.product?.variants?.find(
                    (variant) => variant.options[changedIndex] === value
                )
            );
        },

        updateURL() {
            const id = this.currentVariant?.id;
            if (!id) {
                return;
            }

            const url = new URL(window.location.href);
            url.searchParams.set('variant', id);
            window.history.replaceState({}, '', url);
        },

        updatePrices() {
            const { container, currentVariant } = this;
            if (!container || !currentVariant) {
                return;
            }

            const price = currentVariant.price;
            const compareAtPrice = currentVariant.compare_at_price;
            const onSale = compareAtPrice && compareAtPrice > price;

            container.querySelectorAll(selectors.saleRetailPrice).forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(price, ACTIVE_CURRENCY, false, true);
            });

            container.querySelectorAll(selectors.saleCompareAtPrice).forEach((element) => {
                if (onSale) {
                    element.innerHTML = moneyWithCurrencyProvided(
                        compareAtPrice,
                        ACTIVE_CURRENCY,
                        false,
                        true
                    );
                    element.removeAttribute('hidden');
                } else {
                    element.setAttribute('hidden', '');
                }
            });

            const discountPercent = onSale
                ? Math.round(100 - ((price * 100) / compareAtPrice))
                : 0;

            container.querySelectorAll(selectors.saleDiscountPercent).forEach((element) => {
                element.textContent = discountPercent;
            });

            container.querySelectorAll(selectors.saleTag).forEach((element) => {
                if (onSale) {
                    element.removeAttribute('hidden');
                } else {
                    element.setAttribute('hidden', '');
                }
            });
        },

        updateBuyForm() {
            const { container, currentVariant } = this;
            if (!container || !currentVariant) {
                return;
            }

            const idInput = container.querySelector(selectors.buyVariantId);
            if (idInput) {
                idInput.value = currentVariant.id;
            }

            const button = container.querySelector(selectors.buySubmit);
            if (!button) {
                return;
            }

            const available = !!currentVariant.available;
            button.disabled = !available;
            button.classList.toggle('disabled', !available);

            const text = button.querySelector(selectors.buySubmitText);
            if (text) {
                const label = available
                    ? button.dataset.labelAdd
                    : button.dataset.labelSoldout;
                if (label) {
                    text.textContent = label;
                }
            }
        },

        async updateSlider() {
            const { container, currentVariant } = this;
            if (!container || !currentVariant) {
                return;
            }

            const sliderHTML = container.querySelector(selectors.sliderHtml);
            const handle = container.dataset.productHandle;
            if (!sliderHTML || !handle) {
                return;
            }

            sliderHTML.classList.add('loading');

            const params = new URLSearchParams();
            params.append('view', 'slider-single');
            params.append('variant', currentVariant.id);

            try {
                const html = await fetch(`/products/${handle}?${params.toString()}`)
                    .then((response) => response.text());

                Object.values(this.sliders).forEach((slider) => slider?.destroy?.());
                this.sliders = {};

                sliderHTML.innerHTML = html;

                this.initSlider();
                this.initReviewsSlider();
            } catch {
            } finally {
                sliderHTML.classList.remove('loading');
            }
        },

        initBuyBoxTracking() {
            const { container } = this;
            if (!container) {
                return;
            }

            const buyBlock = container.querySelector(selectors.buyBlock);
            if (!buyBlock) {
                return;
            }

            const floatingBtn = container.querySelector(selectors.tutorialFloatingBtn);
            const mainBtn = container.querySelector(selectors.tutorialMainBtn);

            let ticking = false;
            let mainVisible = false;
            let reachedEnd = false;

            const supportsIntersectionObserver = 'IntersectionObserver' in window;

            const applyFloatingVisibility = () => {
                if (!floatingBtn) {
                    return;
                }
                floatingBtn.classList.toggle('is-visible', !mainVisible && !reachedEnd);
            };

            const update = () => {
                ticking = false;

                const buyRect = buyBlock.getBoundingClientRect();
                reachedEnd = buyRect.bottom < window.innerHeight - 1;

                document.documentElement.classList.toggle('product-buy-stuck', !reachedEnd);

                if (!floatingBtn || !mainBtn) {
                    return;
                }

                if (!supportsIntersectionObserver) {
                    const mainRect = mainBtn.getBoundingClientRect();
                    mainVisible = mainRect.bottom > 0 && mainRect.top < buyRect.top;
                }

                applyFloatingVisibility();
            };

            const requestUpdate = () => {
                if (ticking) {
                    return;
                }
                ticking = true;
                window.requestAnimationFrame(update);
            };

            update();
            window.addEventListener('scroll', requestUpdate, { passive: true });
            window.addEventListener('resize', requestUpdate);

            let mainObserver = null;
            if (mainBtn && supportsIntersectionObserver) {
                const buyOffset =
                    parseInt(
                        getComputedStyle(document.documentElement).getPropertyValue('--product-steps-buy-offset'),
                    ) || 156;

                mainObserver = new IntersectionObserver(
                    (entries) => {
                        mainVisible = entries[0].isIntersecting;
                        applyFloatingVisibility();
                    },
                    { rootMargin: `0px 0px -${buyOffset}px 0px` },
                );
                mainObserver.observe(mainBtn);
            }

            let resizeObserver = null;
            if ('ResizeObserver' in window) {
                resizeObserver = new ResizeObserver(requestUpdate);
                resizeObserver.observe(buyBlock);
            }

            this.buyBoxTracking = { requestUpdate, mainObserver, resizeObserver };
        },

        destroy() {
            if (this.buyBoxTracking) {
                window.removeEventListener('scroll', this.buyBoxTracking.requestUpdate);
                window.removeEventListener('resize', this.buyBoxTracking.requestUpdate);
                this.buyBoxTracking.mainObserver?.disconnect();
                this.buyBoxTracking.resizeObserver?.disconnect();
                this.buyBoxTracking = null;
                document.documentElement.classList.remove('product-buy-stuck');
            }

            if (this.onOptionChangeHandler) {
                this.container?.removeEventListener('change', this.onOptionChangeHandler);
                this.onOptionChangeHandler = null;
            }

            Object.values(this.sliders).forEach((slider) => {
                slider?.destroy?.();
            });
            this.sliders = {};
        },
    };
}

sections.register(SECTION_NAME, {
    section: null,

    onLoad() {
        const { container } = this;
        if (!container) {
            return;
        }

        this.section = ProductSection(container);
        this.section?.onLoad();
    },

    destroy() {
        this.section?.destroy();
    },
});

document.addEventListener('DOMContentLoaded', () => {
    sections.load([SECTION_NAME]);
});
